/**
 * Console soft desk for Gate 4 phone locations — on-call + (optional soak) browser pin.
 * tip551: product GPS path = **App-only**. Browser pin defaults OFF (soak only).
 * Native App GPS remains the field SoT (consent + batch → Home / Admin tree).
 */

import {
  LOCATIONS_CONSOLE_TIMEOUT_MS,
  buildLocationsBatchPath,
  buildLocationsConsentPostPath,
  buildLocationsOnCallGetPath,
  buildLocationsOnCallPutPath,
  buildPhoneLocationBatchBody,
} from "./locations-console-api.js";
import { softApiError } from "./format.js";

/** tip551 — set true only for explicit soak; never product default. */
export const LOCATIONS_SOFT_DESK_BROWSER_PIN_ENABLED = false;

/**
 * @param {ParentNode} root
 * @param {{ api: Function, toast: Function, escapeHtml: Function }} ctx
 * @returns {() => void}
 */
export function wireLocationsSoftDesk(root, ctx) {
  const { api, toast, _escapeHtml } = ctx;
  const host = root.querySelector("[data-locations-soft-desk]");
  if (!host || typeof api !== "function") return () => {};

  const statusEl = host.querySelector("[data-loc-status]");
  const onCallEl = host.querySelector("[data-loc-oncall]");
  const shareBtn = host.querySelector("[data-loc-share]");
  const onCallBtn = host.querySelector("[data-loc-toggle-oncall]");

  const setStatus = (msg) => {
    if (statusEl) statusEl.textContent = String(msg || "");
  };

  const refreshOnCall = async () => {
    try {
      const pack = await api(buildLocationsOnCallGetPath(), { timeoutMs: LOCATIONS_CONSOLE_TIMEOUT_MS });
      const on = !!(pack && (pack.on_call === true || pack.status === "on_call" || pack.on_call === 1));
      if (onCallEl) onCallEl.textContent = on ? "On call" : "Off call";
      if (onCallBtn) onCallBtn.textContent = on ? "Go off call" : "Go on call";
      onCallBtn && (onCallBtn.dataset.on = on ? "1" : "0");
    } catch (e) {
      if (onCallEl) onCallEl.textContent = "On-call desk closed";
      setStatus(softApiError(e, "Location not open yet for this account."));
    }
  };

  const onToggle = async () => {
    const next = onCallBtn && onCallBtn.dataset.on === "1" ? false : true;
    try {
      await api(buildLocationsOnCallPutPath(), {
        method: "PUT",
        body: { on_call: next },
        timeoutMs: LOCATIONS_CONSOLE_TIMEOUT_MS,
      });
      toast(next ? "You are on call" : "You are off call", "ok");
      await refreshOnCall();
    } catch (e) {
      toast(softApiError(e, "Could not update on-call."), "err");
    }
  };

  const onShare = async () => {
    if (!LOCATIONS_SOFT_DESK_BROWSER_PIN_ENABLED) {
      setStatus("Browser pin is off (tip551). Share GPS from the App — Home map is App→PG SoT.");
      toast("App GPS only — Console browser pin demoted.", "err");
      return;
    }
    setStatus("Requesting browser location…");
    if (!navigator.geolocation) {
      setStatus("This browser cannot share a pin.");
      return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await api(buildLocationsConsentPostPath(), {
          method: "POST",
          body: { purpose: "dispatch", retention_hours: 24 },
          timeoutMs: LOCATIONS_CONSOLE_TIMEOUT_MS,
        });
      } catch (_) {
        /* consent may already exist */
      }
      try {
        const body = buildPhoneLocationBatchBody({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
        });
        await api(buildLocationsBatchPath(), {
          method: "POST",
          body,
          timeoutMs: LOCATIONS_CONSOLE_TIMEOUT_MS,
        });
        toast("Pin shared on the map", "ok");
        setStatus("Soak pin shared — App GPS remains the product path.");
      } catch (e) {
        toast(softApiError(e, "Could not share pin."), "err");
        setStatus(softApiError(e, "Share failed — location tools may be off."));
      }
    }, () => {
      setStatus("Location permission denied — enable it in the browser, or share GPS from the App.");
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 });
  };

  if (onCallBtn) onCallBtn.addEventListener("click", onToggle);
  if (shareBtn) {
    if (!LOCATIONS_SOFT_DESK_BROWSER_PIN_ENABLED) {
      shareBtn.disabled = true;
      shareBtn.setAttribute("aria-disabled", "true");
      shareBtn.title = "Demoted tip551 — App GPS only";
      shareBtn.textContent = "Browser pin off";
    }
    shareBtn.addEventListener("click", onShare);
  }
  void refreshOnCall();
  if (!LOCATIONS_SOFT_DESK_BROWSER_PIN_ENABLED) {
    setStatus("Product path: App GPS (consent + batch). Console browser pin is soak-only and OFF.");
  }

  return () => {
    if (onCallBtn) onCallBtn.removeEventListener("click", onToggle);
    if (shareBtn) shareBtn.removeEventListener("click", onShare);
  };
}

/** Soft HTML block for Admin · Relationship + locations (tip551 demoted share). */
export function locationsSoftDeskHtml() {
  const pinOff = !LOCATIONS_SOFT_DESK_BROWSER_PIN_ENABLED;
  return `
  <section class="glass-panel glass-panel--inset" data-locations-soft-desk="1" aria-label="Location" style="margin-top:14px;padding:14px 16px">
    <p class="acc-settings-panel__title" style="margin:0 0 4px">${"" /* tip551 */}Field</p>
    <h4 class="acc-settings-panel__head" style="margin:0;font-size:14px">My location</h4>
    <p class="muted settings-admin-desc" style="margin:6px 0 0">App GPS is the product path. Browser pin is soak-only${pinOff ? " and currently <strong>OFF</strong>" : ""}.</p>
    <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:12px;align-items:center">
      <span class="badge neutral" data-loc-oncall>On-call…</span>
      <button type="button" class="btn sm secondary btn-tap" data-loc-toggle-oncall>Go on call</button>
      <button type="button" class="btn sm ${pinOff ? "ghost" : ""} btn-tap" data-loc-share${pinOff ? " disabled aria-disabled=\"true\"" : ""}>${pinOff ? "Browser pin off" : "Share my pin (soak)"}</button>
      <button type="button" class="btn sm ghost btn-tap" data-cc-go="overview">Open Home map</button>
    </div>
    <p class="muted" data-loc-status style="margin:10px 0 0;font-size:13px;line-height:1.45" aria-live="polite"></p>
  </section>`;
}
